import ConsumableIcon from "@/components/icons/ConsumableIcon";
import ElectricityIcon from "@/components/icons/ElectricityIcon";
import EolIcon from "@/components/icons/chapters/eolIcon";
import List from "@/components/visualizations/List/List";
import useTranslation from "@/hooks/useTranslation";
import { useCustomerImpactPerType } from "@/pages/dashboard/hooks/useCustomerImpact";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { formatAmountWithUnit } from "@/pages/dashboard/utils/formatters";
import { useEffect, useState } from "react";
import Widget from "../../components/Widget/Widget";
import { ImpactMetric } from "../../utils/types";

const step = 5;

export default function CustomerImpactTopContributors({ metric }: { metric: ImpactMetric }) {
  const [limit, setLimit] = useState(step);
  const { startDate, endDate } = useDateRange();
  const translation = useTranslation();

  useEffect(() => {
    setLimit(step);
  }, [startDate, endDate]);

  const { data, isLoading, isFetching } = useCustomerImpactPerType({
    pagination: { page: 1, pageSize: limit + 5 },
    sort: {
      key: metric.key,
      order: "descend",
    },
    metric: metric.key,
  });

  const numMore = Math.min((data?.customer_impact.length || 0) - limit);

  const listData = data?.customer_impact
    ? data?.customer_impact.slice(0, limit).map((row) => {
        return {
          icon:
            row.impact_type === "electricity" ? (
              <ElectricityIcon />
            ) : row.impact_type === "eol" ? (
              <EolIcon />
            ) : (
              <ConsumableIcon />
            ),
          label: row.name,
          value: row[metric.key],
          extraProp: {
            label: "Amount",
            value: formatAmountWithUnit(row.quantity, row.unit, 0),
          },
        };
      })
    : [];

  return (
    <Widget
      title={"Top contributing customer impact"}
      tooltip={translation["dashboard"]["customer"]["top_contributors"]}
    >
      <List
        loading={isLoading || isFetching}
        data={{
          list: listData,
          total: data?.company_customer_impact[metric.key],
        }}
        loadMore={{
          show: numMore > 0,
          numberOfMore: numMore,
          fn: () => setLimit(limit + step),
        }}
        format={metric.formatter}
      />
    </Widget>
  );
}
